/**
 * SAT Iframe Bridge - Handles communication with the capture widget iframe.
 *
 * The widget sends captured selfies as native File objects inside
 * payload.files[], identified by role ('plain' always present, 'annotated'
 * optional). Blobs are held in memory here - they are not serializable, so
 * they never go into sessionStorage.
 */
(function (global) {
  "use strict";

  var config = {
    capturePageUrl: "",
    widgetOrigin: "",
    wrapEl: null,
    frameEl: null,
    // Pristine, never-navigated copy of the iframe, taken before the first
    // load. reset() rebuilds from this - see the note there.
    frameProto: null,
  };

  var iframeLoaded = false;
  var messageBound = false;
  var capture = null;
  var overlayOpen = false;

  // Verbose per-message logging. The switch itself lives in sat-api.js, so
  // the whole flow turns on together - ?satDebug=true, or
  // localStorage.setItem('satDebug', 'true') to persist across navigations.
  // The fallback keeps this module working if it is ever loaded without it.
  function debug() {
    if (global.SATDebug && typeof global.SATDebug.enabled === "function") {
      return global.SATDebug.enabled();
    }
    try {
      return global.localStorage.getItem("satDebug") === "true";
    } catch (e) {
      return false;
    }
  }

  function describeFiles(files) {
    return (files || []).map(function (file) {
      return {
        role: file.role,
        field: file.field,
        filename: file.filename,
        mimeType: file.mimeType,
        reportedSize: file.size,
        blobType: file.blob ? Object.prototype.toString.call(file.blob) : null,
        blobSize: file.blob ? file.blob.size : null,
        isFile: typeof File !== "undefined" && file.blob instanceof File,
      };
    });
  }

  function originOf(url) {
    if (!url) return "";
    try {
      return new URL(url, global.location.href).origin;
    } catch (e) {
      return "";
    }
  }

  function loadFrame(forceReload) {
    var frameEl = config.frameEl;
    if (!frameEl) return;

    if (!config.capturePageUrl) {
      console.error("[SAT Iframe] No widget URL configured");
      return;
    }

    var url = config.capturePageUrl;
    if (forceReload) {
      url += (url.indexOf("?") >= 0 ? "&" : "?") + "v=" + Date.now();
    }
    frameEl.src = url;

    frameEl.onerror = function (err) {
      console.error("[SAT Iframe] Frame load error:", err);
    };
  }

  function postToWidget(message) {
    var frameEl = config.frameEl;
    if (!frameEl || !frameEl.contentWindow || !config.widgetOrigin) return;
    frameEl.contentWindow.postMessage(message, config.widgetOrigin);
  }

  function sendAck(requestId, ok, error) {
    var message = {
      source: "sat-parent",
      type: "SAT_CAPTURE_ACK",
      requestId: requestId,
      ok: ok,
    };
    if (error) message.error = error;
    if (debug()) {
      console.log(
        "[SAT Iframe] -> SAT_CAPTURE_ACK",
        message,
        "to",
        config.widgetOrigin,
      );
    }
    postToWidget(message);
  }

  function emitError(code, message) {
    global.document.dispatchEvent(
      new CustomEvent("sat:error", {
        detail: { code: code, message: message },
      }),
    );
  }

  function handleCapture(data) {
    var payload = data.payload;
    var requestId = data.requestId;

    if (debug()) {
      console.group("[SAT Iframe] SAT_CAPTURE received");
      console.log("requestId:", requestId);
      console.log("transport:", payload && payload.transport);
      console.log("mimeType:", payload && payload.mimeType);
      console.log(
        "dimensions:",
        payload ? payload.width + " x " + payload.height : null,
      );
      console.log("capturedAt:", payload && payload.capturedAt);
      console.log(
        "file count:",
        payload && payload.files ? payload.files.length : 0,
      );
      if (payload && payload.files) {
        var described = describeFiles(payload.files);
        if (console.table) console.table(described);
        else console.log("files:", described);
      }
      console.log("raw payload:", payload);
      console.groupEnd();
    }

    if (!payload || !payload.files || !payload.files.length) {
      sendAck(requestId, false, "No files in capture payload");
      emitError("INVALID_IMAGE", "Failed to capture photo. Please try again.");
      return;
    }

    var plain = payload.files.find(function (file) {
      return file.role === "plain";
    });

    if (!plain || !plain.blob) {
      sendAck(requestId, false, "Missing plain capture file");
      emitError("INVALID_IMAGE", "Failed to capture photo. Please try again.");
      return;
    }

    var annotated = payload.files.find(function (file) {
      return file.role === "annotated";
    });
    if (debug()) {
      console.log(
        "[SAT Iframe] resolved roles - plain:",
        plain.filename,
        "| annotated:",
        annotated
          ? annotated.filename
          : "NONE (will fall back to raw for analyzed_image)",
      );
    }

    capture = payload;
    sendAck(requestId, true);

    global.SATIframeBridge.close();

    global.document.dispatchEvent(
      new CustomEvent("sat:photo", { detail: payload }),
    );
  }

  function handleMessage(event) {
    if (config.widgetOrigin && event.origin !== config.widgetOrigin) {
      if (debug()) {
        console.debug(
          "[SAT Iframe] ignored message from",
          event.origin,
          "(expected " + config.widgetOrigin + ")",
          event.data,
        );
      }
      return;
    }

    var data = event.data;
    if (!data || typeof data !== "object") return;

    if (data.source !== "sat-widget") {
      if (debug()) {
        console.debug("[SAT Iframe] ignored source:", data.source, data);
      }
      return;
    }

    if (debug()) console.debug("[SAT Iframe] <-", data.type, data);

    switch (data.type) {
      case "SAT_READY":
        if (debug()) console.log("[SAT Iframe] widget ready");
        break;

      case "SAT_CAPTURE":
        handleCapture(data);
        break;

      case "SAT_ERROR":
        var errorPayload = data.payload || {};
        console.error(
          "[SAT Iframe] SAT_ERROR",
          errorPayload.code,
          "-",
          errorPayload.message,
        );
        // The widget can report a camera failure after we have closed the
        // overlay - most visibly when the page is restored from the back /
        // forward cache and its camera stream is gone. Surfacing that would
        // put a capture error on a screen the user is not capturing from.
        if (!overlayOpen) {
          if (debug()) {
            console.log("[SAT Iframe] error ignored - overlay is not open");
          }
          break;
        }
        emitError(errorPayload.code, errorPayload.message);
        break;
    }
  }

  global.SATIframeBridge = {
    configure: function (opts) {
      config.capturePageUrl = opts.capturePageUrl || "";
      config.widgetOrigin = originOf(config.capturePageUrl);
      config.wrapEl = opts.wrapEl;
      config.frameEl = opts.frameEl;
      if (opts.frameEl && !config.frameProto) {
        config.frameProto = opts.frameEl.cloneNode(false);
      }

      if (!messageBound) {
        messageBound = true;
        global.addEventListener("message", handleMessage);
      }
    },

    /**
     * The most recent capture payload, or null. Contains files[] with native
     * File objects - only valid for the lifetime of this page.
     */
    getCapture: function () {
      return capture;
    },

    /** Seed a capture from outside the widget, e.g. a gallery upload. */
    setCapture: function (payload) {
      capture = payload;
    },

    getFile: function (role) {
      if (!capture || !capture.files) return null;
      return (
        capture.files.find(function (file) {
          return file.role === role;
        }) || null
      );
    },

    clearCapture: function () {
      capture = null;
    },

    open: function (forceReload) {
      var wrapEl = config.wrapEl;
      var frameEl = config.frameEl;
      if (!wrapEl || !frameEl) return;

      overlayOpen = true;
      wrapEl.classList.add("sat-widget-wrap--open");
      wrapEl.setAttribute("aria-hidden", "false");

      if (forceReload || !iframeLoaded) {
        loadFrame(forceReload);
        iframeLoaded = true;
      }
    },

    close: function () {
      overlayOpen = false;

      var wrapEl = config.wrapEl;
      if (wrapEl) {
        wrapEl.classList.remove("sat-widget-wrap--open");
        wrapEl.setAttribute("aria-hidden", "true");
      }

      // Unload the frame rather than just hiding it. A hidden iframe keeps the
      // widget alive and the camera held open - the recording indicator stays
      // lit, it drains battery, and it can emit errors long after the user has
      // moved on. Captured File objects stay usable once the frame is gone.
      this.reset();

      global.document.dispatchEvent(new CustomEvent("sat:closed"));
    },

    /**
     * Discard the iframe element and put a fresh one in its place.
     *
     * Dropping the src attribute is not enough: it does not navigate, so the
     * widget document stays the frame's active document and the next src
     * assignment is a real navigation, which pushes an entry onto the joint
     * session history. That entry belongs to the frame, so backing onto it
     * fires popstate inside the frame and not here - the parent screen does
     * not move and the back looks dead until a second press.
     *
     * Discarding the browsing context removes the entries it owns, and a
     * never-navigated frame makes the next load a replacing initial
     * navigation, so a capture session leaves no history behind either way.
     */
    reset: function () {
      iframeLoaded = false;
      var frameEl = config.frameEl;
      if (!frameEl || !config.frameProto || !frameEl.parentNode) return;

      var fresh = config.frameProto.cloneNode(false);
      frameEl.parentNode.replaceChild(fresh, frameEl);
      config.frameEl = fresh;
    },
  };
})(window);
